import React, { useState } from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import PageRequest from '@app/model/_common/paging/PageRequest';
import { useQueries } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';
import FormButton from '@app/component/form/button/FormButton';
import PriceSubmissionDetailsDrawer
	from './overlay/PriceSubmissionDetailsDrawer';
import PricingSubmission from '@app/model/pricing-automation/PricingSubmission';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';


const PricingSubmissionsLogPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const [pageRequest, setPageRequest] = useState<PageRequest>({
		page: 0,
		size: 30,
		sort: { field: 'CREATION_DATE', direction: 'DESC' },
	});
	const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
	const next = () => setPageRequest({ ...pageRequest, page: pageRequest.page + 1 });
	const previous = () => setPageRequest({ ...pageRequest, page: pageRequest.page - 1 });

	const results = useQueries({
		queries: [
			{
				queryKey: PricingAutomationQueryKeys.pricingRooms(companyHotel.id),
				queryFn: () => PricingAutomationService.findCompanyHotelPricingRooms(companyHotel.id),
				enabled: !!companyHotel?.id,
			}, {
				queryKey: PricingAutomationQueryKeys.pageCompanyHotelPricingSubmissions(companyHotel.id, pageRequest),
				queryFn: () => PricingAutomationService.pageCompanyHotelPricingSubmissions(companyHotel.id, pageRequest),
				enabled: !!companyHotel?.id,
			}],
	});

	if (results.some(result => result.isLoading))
		return <TablePlaceholder />;

	if (results.some(result => result.isError))
		return <Message state="error">Error loading price submission logs. Please try again later!</Message>;

	const rooms = results[0].data;
	const page = results[1].data;

	if (!rooms || !page || page.isEmpty)
		return <Message state="info">No price submission logs found.</Message>;

	const formatOptionalDateTime = (date?: string | null) => date ? Utilities.formatDateString(date, 'DD/MM/yyyy HH:mm:ss') : '-';
	const formatCompactDateTime = (date?: string | null) => date ? Utilities.formatDateString(date, 'DD/MM HH:mm') : '-';
	const formatStatDateRange = (submission: PricingSubmission) => {
		const firstStatDate = submission.getFirstStatDate();
		const lastStatDate = submission.getLastStatDate();
		if (!firstStatDate || !lastStatDate) return '-';
		if (firstStatDate === lastStatDate) return Utilities.formatDateString(firstStatDate, 'DD/MM/yyyy', false);
		return `${Utilities.formatDateString(firstStatDate, 'DD/MM/yyyy', false)} - ${Utilities.formatDateString(lastStatDate, 'DD/MM/yyyy', false)}`;
	};

	return <>
		<div className="mt-6 pb-1 mb-3">
			<div className="sm:flex items-center justify-between">
				<div className="flex items-center sm:space-x-5 mb-2 sm:mb-0">
					<h1 className="text-lg font-bold leading-6">Price Changes</h1>
				</div>
			</div>
		</div>
		<div className="bg-white rounded-xl shadow">
			<div className="overflow-x-auto overflow-y-auto rounded-xl max-h-[50dvh]">
				<div className="inline-block min-w-full align-middle">
					<table className="min-w-full divide-y divide-gray-300">
						<thead className="bg-gray-50">
						<tr className="divide-x divide-gray-200">
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Submitted Date</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Submitted By</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Period</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"># of Days Affected</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Method</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
							<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"></th>
						</tr>
						</thead>
						<tbody className="divide-y divide-gray-200">
						{page!.content.map(submission => {


							return <tr key={submission.id} className="divide-x divide-gray-200">
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500" title={`Created: ${formatOptionalDateTime(submission.creationDate)}\nSubmitted: ${formatOptionalDateTime(submission.submissionDate)}\nFinished: ${formatOptionalDateTime(submission.finishDate)}`}>
									{formatOptionalDateTime(submission.finishDate)}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
									{submission.getSubmitterName()}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
									{formatStatDateRange(submission)}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
									{submission.getNumberOfDays()}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
									<Tag state="info">{Utilities.snakeCaseToWords(submission.type)}</Tag>
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">
									{submission.hasFailed() && <Tag state="critical">Failed</Tag>}
									{submission.hasSucceeded() && <Tag state="success">Succeeded</Tag>}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-sm">
									<a onClick={() => setSelectedSubmissionId(submission.id)}
									   className="text-blue-600 font-semibold hover:text-blue-900 cursor-pointer">
										Open ({submission.getNumberOfDays()})
									</a>
								</td>
							</tr>;
						})}
						</tbody>
					</table>
				</div>
				<nav aria-label="Pagination"
				     className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
					<div className="hidden sm:block">
						<p className="text-sm text-gray-700">
							Showing <span className="font-medium">{(pageRequest.page * pageRequest.size) + 1}</span> to <span
							className="font-medium">{(pageRequest.page * pageRequest.size) + page.content.length}</span> of <span
							className="font-medium">{page.totalElements}</span> results
						</p>
					</div>
					<div className="flex flex-1 justify-between sm:justify-end space-x-3">
						<FormButton state="white" onClick={previous} disabled={pageRequest.page === 0}>Previous</FormButton>
						<FormButton state="white" onClick={next} disabled={page.isLastPage}>Next</FormButton>
					</div>
				</nav>
			</div>
		</div>
		{selectedSubmissionId && <PriceSubmissionDetailsDrawer submissionId={selectedSubmissionId} onClose={() => setSelectedSubmissionId(null)}/>}
	</>;
});

export default PricingSubmissionsLogPage;
